import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { formatMerchandiseType } from '../../utils/formatters';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Slider } from '../ui/slider';
import { 
  CheckCircle, 
  XCircle, 
  MessageSquare, 
  DollarSign,
  AlertTriangle,
  FileText,
  Download
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const QuoteValidationModal = ({ 
  isOpen, 
  onClose, 
  operation,
  onSuccess 
}) => {
  const [action, setAction] = useState(null); // 'accept', 'refuse', 'negotiate'
  const [loading, setLoading] = useState(false);
  const [refuseReason, setRefuseReason] = useState('');
  const [negotiatedPrice, setNegotiatedPrice] = useState(null);
  const [negotiationMessage, setNegotiationMessage] = useState('');
  
  // Quote details
  const quotePrice = operation?.quote_details?.total_ttc || operation?.estimatedPrice || 0;
  const minPrice = quotePrice * 0.90; // 10% minimum discount
  const maxPrice = quotePrice * 1.10; // Max 10% increase

  useEffect(() => {
    if (isOpen && operation) {
      setNegotiatedPrice(quotePrice);
    }
  }, [isOpen, operation, quotePrice]);

  const handleAccept = async () => {
    setLoading(true);
    try {
      // Change status to planification (accepted quote)
      await axios.post(
        `${API}/api/operation-status/${operation.id}/change`,
        {
          new_status: 'validee_planification',
          validate_requirements: false
        },
        {
          headers: { 'Authorization': 'Bearer cookie-auth' }
        }
      );
      
      toast({
        title: "✅ Devis accepté",
        description: "Votre devis a été accepté. Nous procéderons à la planification de votre transport.",
      });
      
      if (onSuccess) onSuccess();
      onClose();
      
    } catch (error) {
      console.error('Error accepting quote:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible d'accepter le devis",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefuse = async () => {
    if (!refuseReason.trim()) {
      toast({
        title: "Motif requis",
        description: "Veuillez indiquer pourquoi vous refusez ce devis",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    try {
      // Change status to cancelled by client
      await axios.post(
        `${API}/api/operation-status/${operation.id}/change`,
        {
          new_status: 'annulee_par_client',
          validate_requirements: false,
          reason: refuseReason
        },
        {
          headers: { 'Authorization': 'Bearer cookie-auth' }
        }
      );
      
      toast({
        title: "Devis refusé",
        description: "Votre refus a été enregistré. Notre équipe en sera informée.",
      });
      
      if (onSuccess) onSuccess();
      onClose();
      
    } catch (error) {
      console.error('Error refusing quote:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de refuser le devis",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNegotiate = async () => {
    if (!negotiationMessage.trim()) {
      toast({
        title: "Message requis",
        description: "Veuillez expliquer votre proposition de tarif",
        variant: "destructive"
      });
      return;
    }
    
    setLoading(true);
    try {
      // Use the negotiate endpoint instead of status change
      // This works correctly whether we're already in negotiation or not
      await axios.post(
        `${API}/api/transport-requests/${operation.id}/negotiate`,
        {
          proposedPrice: negotiatedPrice,
          message: negotiationMessage
        },
        {
          headers: { 'Authorization': 'Bearer cookie-auth' }
        }
      );
      
      toast({
        title: "💬 Proposition envoyée",
        description: "Votre proposition de tarif a été transmise à notre équipe commerciale.",
      });
      
      if (onSuccess) onSuccess();
      onClose();
      
    } catch (error) {
      console.error('Error negotiating quote:', error);
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible d'envoyer la proposition",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadQuote = () => {
    // TODO: Implement quote PDF download
    toast({
      title: "Téléchargement",
      description: "Le téléchargement du devis va commencer...",
    });
  };

  if (!operation) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <FileText className="w-6 h-6 text-emerald-600" />
            Validation de votre devis
          </DialogTitle>
          <DialogDescription>
            Opération #{operation.operationNumber || operation.id?.slice(0, 8)}
          </DialogDescription>
        </DialogHeader>

        {/* Quote Summary */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-4 rounded-lg border border-emerald-200">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Départ</p>
              <p className="font-semibold">{operation.departure}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Arrivée</p>
              <p className="font-semibold">{operation.arrival}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Type de marchandise</p>
              <p className="font-semibold">{formatMerchandiseType(operation.merchandiseType) || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Poids</p>
              <p className="font-semibold">{operation.weight} tonnes</p>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-emerald-300">
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold text-gray-700">Prix Total TTC</span>
              <span className="text-2xl font-bold text-emerald-600">
                {quotePrice.toLocaleString('fr-FR')} FCFA
              </span>
            </div>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            className="mt-3 w-full"
            onClick={downloadQuote}
          >
            <Download className="w-4 h-4 mr-2" />
            Télécharger le devis PDF
          </Button>
        </div>

        {/* Action Selection */}
        {!action && (
          <div className="space-y-3">
            <Label className="text-base font-semibold">Choisissez une action :</Label>
            
            {/* Accept */}
            <Button
              onClick={() => setAction('accept')}
              className="w-full h-auto py-4 flex items-start gap-3 bg-green-50 hover:bg-green-100 text-green-900 border-2 border-green-300 hover:border-green-400"
              variant="outline"
            >
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div className="text-left flex-1">
                <p className="font-semibold">Accepter le devis</p>
                <p className="text-sm text-green-700 font-normal">
                  Je valide ce devis et souhaite procéder à la planification
                </p>
              </div>
            </Button>
            
            {/* Negotiate - only shown if enabled by admin */}
            {operation?.negotiationEnabled && (
            <Button
              onClick={() => setAction('negotiate')}
              className="w-full h-auto py-4 flex items-start gap-3 bg-blue-50 hover:bg-blue-100 text-blue-900 border-2 border-blue-300 hover:border-blue-400"
              variant="outline"
            >
              <MessageSquare className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div className="text-left flex-1">
                <p className="font-semibold">Négocier le tarif</p>
                <p className="text-sm text-blue-700 font-normal">
                  Je souhaite proposer un autre tarif
                </p>
              </div>
            </Button>
            )}
            
            {/* Refuse */}
            <Button
              onClick={() => setAction('refuse')}
              className="w-full h-auto py-4 flex items-start gap-3 bg-red-50 hover:bg-red-100 text-red-900 border-2 border-red-300 hover:border-red-400"
              variant="outline"
            >
              <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div className="text-left flex-1">
                <p className="font-semibold">Refuser le devis</p>
                <p className="text-sm text-red-700 font-normal">
                  Je ne souhaite pas donner suite à cette demande
                </p>
              </div>
            </Button>
          </div>
        )}

        {/* Accept Confirmation */}
        {action === 'accept' && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-1">
                    Confirmer l'acceptation
                  </h4>
                  <p className="text-sm text-green-700">
                    En acceptant ce devis, vous confirmez votre accord avec le tarif de <strong>{quotePrice.toLocaleString('fr-FR')} FCFA</strong>.
                    Nous procéderons à la planification de votre transport dans les plus brefs délais.
                  </p>
                </div>
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setAction(null)}>
                Retour
              </Button>
              <Button
                onClick={handleAccept}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? 'Confirmation...' : 'Confirmer l\'acceptation'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Refuse Form */}
        {action === 'refuse' && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-red-900 mb-1">
                    Motif du refus
                  </h4>
                  <p className="text-sm text-red-700">
                    Veuillez nous indiquer la raison de votre refus. Cela nous aidera à mieux comprendre vos besoins.
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <Label htmlFor="refuseReason">Motif (requis)</Label>
              <Textarea
                id="refuseReason"
                value={refuseReason}
                onChange={(e) => setRefuseReason(e.target.value)}
                placeholder="Ex: Prix trop élevé, délais trop longs, etc."
                rows={4}
                className="mt-1"
              />
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setAction(null)}>
                Retour
              </Button>
              <Button
                onClick={handleRefuse}
                disabled={loading || !refuseReason.trim()}
                variant="destructive"
              >
                {loading ? 'Refus en cours...' : 'Confirmer le refus'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Negotiate Form */}
        {action === 'negotiate' && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <DollarSign className="w-6 h-6 text-blue-600 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 mb-1">
                    Proposition de tarif
                  </h4>
                  <p className="text-sm text-blue-700">
                    Utilisez le slider ci-dessous pour proposer votre tarif. Une marge de ±10% est acceptée.
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Tarif proposé</Label>
                <Badge variant="outline" className="text-lg font-bold">
                  {negotiatedPrice?.toLocaleString('fr-FR')} FCFA
                </Badge>
              </div>
              
              <Slider
                value={[negotiatedPrice || quotePrice]}
                onValueChange={(values) => setNegotiatedPrice(values[0])}
                min={minPrice}
                max={maxPrice}
                step={1000}
                className="my-4"
              />
              
              <div className="flex justify-between text-xs text-gray-500">
                <span>Min: {minPrice.toLocaleString('fr-FR')} FCFA</span>
                <span>Max: {maxPrice.toLocaleString('fr-FR')} FCFA</span>
              </div>
              
              <div className="mt-2 text-center">
                {negotiatedPrice < quotePrice && (
                  <Badge className="bg-green-100 text-green-700 border-0">
                    -{((1 - negotiatedPrice / quotePrice) * 100).toFixed(1)}% du tarif initial
                  </Badge>
                )}
                {negotiatedPrice > quotePrice && (
                  <Badge className="bg-orange-100 text-orange-700 border-0">
                    +{((negotiatedPrice / quotePrice - 1) * 100).toFixed(1)}% du tarif initial
                  </Badge>
                )}
              </div>
            </div>
            
            <div>
              <Label htmlFor="negotiationMessage">Message d'accompagnement (requis)</Label>
              <Textarea
                id="negotiationMessage"
                value={negotiationMessage}
                onChange={(e) => setNegotiationMessage(e.target.value)}
                placeholder="Expliquez pourquoi vous proposez ce tarif..."
                rows={4}
                className="mt-1"
              />
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setAction(null)}>
                Retour
              </Button>
              <Button
                onClick={handleNegotiate}
                disabled={loading || !negotiationMessage.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? 'Envoi...' : 'Envoyer la proposition'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuoteValidationModal;
